import * as THREE from 'three';
import { Scene } from './Scene';
import { PhysicsWorld } from './PhysicsWorld';
import { Car } from './Car';
import { Ball } from './Ball';
import { Stadium } from './Stadium';
import { InputManager } from './InputManager';
import { UI } from './UI';
import { Bot } from './Bot';
import { BoostPad } from './BoostPad';
import * as CANNON from 'cannon-es';
export class Game {
  constructor() {
    // Enable debugging in production too to fix the key detection debug display
    this.debug = true;
    
    if (this.debug) {
      console.log('Game constructor called');
    }
    
    // Set global reference to game instance
    window.game = this;
    
    this.clock = new THREE.Clock();
    this.isGameActive = false;
    this.isPaused = false;
    this.username = 'Player'; // Default username
    this.score = { blue: 0, orange: 0 };
    this.gameMode = '1v1'; // Default to 1v1 mode
    
    // Default car color - can be overridden by URL params
    this.playerCarColor = 'blue';
    
    // Game timer properties
    this.gameTime = 300; // 5 minutes in seconds
    this.timeRemaining = this.gameTime;
    this.countdownTime = 3; // 3 second countdown
    this.isCountdownActive = false;
    this.isGameStarted = false;

    // Game states
    this.states = {
      MODE_SELECT: 'mode_select',
      COUNTDOWN: 'countdown',
      GAME: 'game',
      GOAL: 'goal',
      GAME_OVER: 'game_over'
    };
    this.currentState = this.states.MODE_SELECT;

    // Check for portal parameters
    this.checkPortalParameters();

    // Initialize UI
    this.ui = new UI(this);
    
    // Show mode select screen at startup
    this.ui.showModeSelectScreen();

    // Bind methods
    this.update = this.update.bind(this);
    this.onResize = this.onResize.bind(this);
    this.updateBotAI = this.updateBotAI.bind(this);
    this.updateTimer = this.updateTimer.bind(this);
    this.startCountdown = this.startCountdown.bind(this);
    this.startGame = this.startGame.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.updateBoostSystem = this.updateBoostSystem.bind(this);
    
    // Add ESC key handler
    document.addEventListener('keydown', this.handleKeyPress);
  }
  
  handleKeyPress(event) {
    // Handle ESC key to pause/show menu
    if (event.key === 'Escape') {
      if (this.isGameActive && !this.isCountdownActive) {
        this.togglePause();
      }
    }
  }
  
  togglePause() {
    this.isPaused = !this.isPaused;
    
    if (this.isPaused) {
      this.ui.showPauseMenu();
    } else {
      this.ui.hidePauseMenu();
    }
  }

  checkPortalParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('portal') === 'true') {
      // Get username if provided
      const username = urlParams.get('username');
      if (username) {
        this.username = username;
      }
      
      // Get car color if provided
      const color = urlParams.get('color');
      if (color) {
        this.playerCarColor = color;
      }
      
      // Get game mode if provided (default to 1v1 if not specified)
      const mode = urlParams.get('mode');
      if (mode && ['1v1', '2v2'].includes(mode)) {
        this.gameMode = mode;
      }
      
      // Auto-start game if coming from portal
      this.isGameStarted = true;
      setTimeout(() => this.startGame(), 500);
    }
  }

  init() {
    if (this.debug) {
      console.log('Game initialization started with DIRECT activation approach');
    }
    
    // CRITICAL: CLEANUP PREVIOUS INSTANCES
    // This prevents any lingering references or state from affecting the new game
    if (this.scene) {
      this.scene.cleanup && this.scene.cleanup();
    }
    if (this.physicsWorld) {
      this.physicsWorld.cleanup && this.physicsWorld.cleanup();
    }
    if (this.inputManager) {
      this.inputManager.cleanup();
    }
    
    // Remove any existing cars and ball references
    // Don't attempt to remove meshes as the scene has already been cleaned up
    this.playerCar = null;
    this.aiCars = [];
    this.ball = null;
    
    // Create scene FIRST to ensure it's ready
    this.scene = new Scene();
    
    // Create physics world SECOND with debug enabled
    this.physicsWorld = new PhysicsWorld(this);
    this.physicsWorld.game = this; // Ensure back-reference is set
    
    // Create stadium THIRD as the basis for gameplay
    this.stadium = new Stadium(this.scene, this.physicsWorld);
    
    // Create ball FOURTH with direct debug output
    this.ball = new Ball(this.scene, this.physicsWorld);
    console.log('Ball created at position:', 
      this.ball.body.position.x,
      this.ball.body.position.y,
      this.ball.body.position.z
    );
    
    // Create player car FIFTH
    console.log('Creating player car with DIRECT physics activation');
    this.playerCar = new Car(this.scene, this.physicsWorld, this.playerCarColor, { x: -5, y: 1, z: 0 });
    
    // CRITICAL: Force car to be in active state
    this.playerCar.body.type = CANNON.Body.DYNAMIC;
    this.playerCar.isFrozen = false;
    this.playerCar.body.wakeUp();
    
    console.log('Player car created:', {
      position: [this.playerCar.body.position.x, this.playerCar.body.position.y, this.playerCar.body.position.z],
      isDynamic: this.playerCar.body.type === CANNON.Body.DYNAMIC,
      isFrozen: this.playerCar.isFrozen
    });
    
    // Create AI cars based on game mode
    this.aiCars = [];
    this.bots = [];
    
    if (this.gameMode === '1v1') {
      // Just one opponent for 1v1
      const botCar = new Car(this.scene, this.physicsWorld, 'orange', { x: 5, y: 1, z: 0 });
      // Force AI car to be active too
      botCar.body.type = CANNON.Body.DYNAMIC;
      botCar.isFrozen = false;
      
      this.aiCars = [botCar];
      this.bots = [new Bot(botCar)];
    } else {
      // Full 2v2 mode
      const teammate = new Car(this.scene, this.physicsWorld, this.playerCarColor, { x: -3, y: 1, z: 3 });
      const opponent1 = new Car(this.scene, this.physicsWorld, 'orange', { x: 5, y: 1, z: 0 });
      const opponent2 = new Car(this.scene, this.physicsWorld, 'orange', { x: 3, y: 1, z: 3 });
      
      // Force all AI cars to be active
      [teammate, opponent1, opponent2].forEach(car => {
        car.body.type = CANNON.Body.DYNAMIC;
        car.isFrozen = false;
      });
      
      this.aiCars = [teammate, opponent1, opponent2];
      this.bots = [
        new Bot(teammate),
        new Bot(opponent1),
        new Bot(opponent2)
      ];
    }
    
    // Create boost pads
    this.createBoostPads();
    
    // Initialize input manager with direct debug info
    console.log('Initializing input manager with DIRECT connection to player car');
    try {
      const joystickZone = document.getElementById('joystick-zone');
      
      // Create joystick zone if it doesn't exist
      if (!joystickZone) {
        console.warn('Joystick zone element not found, creating one...');
        const newJoystickZone = document.createElement('div');
        newJoystickZone.id = 'joystick-zone';
        newJoystickZone.className = 'control hidden';
        document.body.appendChild(newJoystickZone);
        
        this.inputManager = new InputManager(newJoystickZone, this.playerCar);
      } else {
        this.inputManager = new InputManager(joystickZone, this.playerCar);
      }
      
      // Initialize input manager with immediate effect
      if (this.inputManager) {
        this.inputManager.init();
        
        // Force initial controls for verification
        this.playerCar.setControls({
          forward: 0,
          backward: 0,
          left: 0,
          right: 0,
          jump: false,
          boost: false
        });
        
        console.log('Input manager initialized and connected directly to player car:', 
          this.playerCar.controls
        );
      }
    } catch (error) {
      console.error('ERROR initializing input manager:', error);
    }
    
    // Event listeners
    window.addEventListener('resize', this.onResize);
    
    // Reset and setup game UI
    this.timeRemaining = this.gameTime;
    this.ui.createTimerDisplay();
    this.ui.updateTimerDisplay(this.timeRemaining);
    this.ui.updateScore(this.score.blue, this.score.orange);
    
    // Camera setup
    this.scene.updateCameraFollowTarget(this.playerCar);
    
    console.log('Game initialization COMPLETE - all systems should be active');
  }

  setUsername(username) {
    this.username = username;
    console.log("Username set to:", username);
  }
  
  setGameMode(mode) {
    this.gameMode = mode;
    
    if (this.debug) {
      console.log('Game mode set:', mode);
    }
  }

  startCountdown() {
    if (this.debug) {
      console.log('Starting countdown');
    }
    
    // Show countdown UI
    this.ui.showCountdown(this.countdownTime);
    
    // Set game state
    this.currentState = this.states.COUNTDOWN;
    this.isCountdownActive = true;
    
    // IMPORTANT CHANGE: Move cars and ball to start positions but DON'T freeze them
    // This ensures player controls are responsive during countdown
    if (this.ball) {
      this.ball.reset(); // Reset position only, don't freeze
    }
    
    if (this.playerCar) {
      // Reposition the player car but don't freeze it
      this.playerCar.reset();
      console.log('Player car reset, controls should be active during countdown');
    }
    
    if (this.aiCars) {
      this.aiCars.forEach(car => car.reset());
    }
    
    // Start countdown
    let count = this.countdownTime;
    const countdownInterval = setInterval(() => {
      count--;
      this.ui.updateCountdown(count);
      
      if (this.debug) {
        console.log('Countdown:', count);
      }
      
      if (count <= 0) {
        clearInterval(countdownInterval);
        this.isCountdownActive = false;
        this.currentState = this.states.GAME;
        
        // Ensure game is active
        this.isGameActive = true;
        this.ui.hideCountdown();
        
        if (this.debug) {
          console.log('Countdown finished, game started');
          
          // Debug player car state
          if (this.playerCar) {
            console.log('Player car state at game start:', {
              isFrozen: this.playerCar.isFrozen,
              bodyType: this.playerCar.body.type,
              controls: {...this.playerCar.controls}
            });
          }
        }
      }
    }, 1000);
  }

  startGame() {
    if (this.debug) {
      console.log('Starting game with simplified flow, mode:', this.gameMode);
    }
    
    // Set game as started immediately
    this.isGameStarted = true;
    this.isGameActive = true; // CRITICAL: Activate the game immediately
    
    // Hide menu screens
    this.ui.hideMenuScreens();
    
    // Reset scores
    this.score = { blue: 0, orange: 0 };
    
    // Initialize all game components
    this.init();
    
    // SKIP COUNTDOWN COMPLETELY - go straight to game
    this.currentState = this.states.GAME;
    
    // Make sure all objects are reset to proper positions
    if (this.ball) {
      this.ball.reset();
    }
    
    if (this.playerCar) {
      this.playerCar.reset();
      
      // Force the car to be responsive to inputs immediately
      this.playerCar.body.type = CANNON.Body.DYNAMIC;
      this.playerCar.isFrozen = false;
      this.playerCar.body.wakeUp();
      
      console.log('Player car initialized directly to playable state:', {
        position: [
          this.playerCar.body.position.x, 
          this.playerCar.body.position.y, 
          this.playerCar.body.position.z
        ],
        isFrozen: this.playerCar.isFrozen,
        type: this.playerCar.body.type
      });
    }
    
    if (this.aiCars) {
      this.aiCars.forEach(car => {
        car.reset();
        car.body.type = CANNON.Body.DYNAMIC;
        car.isFrozen = false;
      });
    }
    
    // Start a brief visual countdown without freezing the game
    this.ui.showCountdown(3);
    let count = 3;
    const countdownInterval = setInterval(() => {
      count--;
      this.ui.updateCountdown(count);
      
      if (count <= 0) {
        clearInterval(countdownInterval);
        this.ui.hideCountdown();
      }
    }, 1000);
    
    if (this.debug) {
      console.log('Game started directly, bypassing countdown freeze');
    }
  }

  update() {
    // CRITICAL: ALWAYS UPDATE REGARDLESS OF STATE
    // Skip only if the game scene is not initialized yet
    if (!this.scene) {
      return;
    }
    
    // Calculate delta time for this frame
    const delta = this.clock.getDelta();
    
    // ALWAYS update physics world - this is critical
    this.physicsWorld.update(delta);
    
    // ALWAYS update all cars - keep them responsive at all times
    if (this.playerCar) {
      // Force player car to dynamic state every frame
      this.playerCar.body.type = CANNON.Body.DYNAMIC;
      this.playerCar.isFrozen = false;
      this.playerCar.update(delta);
      
      // Check for portal collisions
      const portalCollision = this.scene.checkPortalCollisions(this.playerCar);
      if (portalCollision) {
        if (portalCollision.type === 'entrance' || portalCollision.type === 'exit') {
          // Redirect to the portal URL
          console.log('Portal collision detected, redirecting to:', portalCollision.url);
          window.location.href = portalCollision.url;
          return;
        }
      }
      
      // Debug info occasionally
      if (this.debug && Math.random() < 0.01) {
        console.log('Player car state:', {
          position: [
            this.playerCar.body.position.x.toFixed(2),
            this.playerCar.body.position.y.toFixed(2),
            this.playerCar.body.position.z.toFixed(2)
          ],
          velocity: [
            this.playerCar.body.velocity.x.toFixed(2),
            this.playerCar.body.velocity.y.toFixed(2),
            this.playerCar.body.velocity.z.toFixed(2)
          ],
          type: this.playerCar.body.type
        });
      }
    }
    
    // Update AI cars
    if (this.aiCars && this.aiCars.length > 0) {
      this.aiCars.forEach(car => {
        // Force AI cars to be dynamic
        car.body.type = CANNON.Body.DYNAMIC;
        car.isFrozen = false;
        car.update(delta);
      });
    }
    
    // ALWAYS update ball
    if (this.ball) {
      this.ball.body.type = CANNON.Body.DYNAMIC;
      this.ball.update();
    }
    
    // ALWAYS render the scene
    this.scene.render();
    
    // Skip game logic if paused, but everything else still updates
    if (this.isPaused) {
      return;
    }
    
    // ALWAYS update boost system
    this.updateBoostSystem();
    
    // ALWAYS update game timer
    this.updateTimer(delta);
    
    // ALWAYS update bot AI
    this.updateBotAI();
    
    // Check for goals only when in GAME state
    if (this.isGameActive && this.currentState === this.states.GAME) {
      this.checkGoals();
    }
  }
  
  updateTimer(delta) {
    if (this.timeRemaining > 0) {
      this.timeRemaining -= delta;
      if (this.timeRemaining < 0) this.timeRemaining = 0;
      
      // Update UI every second
      if (Math.floor(this.timeRemaining) !== Math.floor(this.timeRemaining + delta)) {
        this.ui.updateTimerDisplay(this.timeRemaining);
      }
      
      // End game when time is up
      if (this.timeRemaining <= 0) {
        this.endGame();
      }
    }
  }
  
  endGame() {
    if (this.debug) {
      console.log('Game over, final score:', this.score);
    }
    
    this.isGameActive = false;
    this.currentState = this.states.GAME_OVER;
    
    // Determine winner
    const winner = this.score.blue > this.score.orange ? 'blue' : 
                  this.score.orange > this.score.blue ? 'orange' : 'tie';
    
    // Show game over UI
    this.ui.showGameOver(winner, this.score);
  }
  
  updateBotAI() {
    // Update all bot AIs if the game is active
    if (!this.ball) {
      return;
    }
    
    // If we have bots, properly update them
    if (this.bots && this.bots.length > 0) {
      for (let i = 0; i < this.bots.length; i++) {
        // Make sure bot exists
        if (this.bots[i]) {
          // Get updated controls from the bot
          const botControls = this.bots[i].update(this.ball, this.timeRemaining);
          
          // Apply the controls to the corresponding car
          if (this.aiCars && this.aiCars[i]) {
            // Force car to be unfrozen to ensure it responds to bot controls
            this.aiCars[i].isFrozen = false;
            
            // Force the car's body to be DYNAMIC
            if (this.aiCars[i].body.type !== CANNON.Body.DYNAMIC) {
              this.aiCars[i].body.type = CANNON.Body.DYNAMIC;
              this.aiCars[i].body.wakeUp();
            }
            
            // Apply bot controls to the car
            this.aiCars[i].setControls(botControls);
            
            // Debug output occasionally
            if (this.debug && Math.random() < 0.01) {
              console.log(`Bot ${i} (${this.aiCars[i].team}) controls:`, botControls);
            }
          }
        }
      }
    }
  }

  checkGoals() {
    // Add safety checks to prevent errors
    if (!this.ball || !this.stadium || !this.stadium.dimensions) return;
    
    const ballPosition = this.ball.getPosition();
    if (!ballPosition) return;
    
    // Instead of simple coordinate check, use a more dynamic goal detection system
    // For blue goal (on the negative X side)
    if (ballPosition.x < -this.stadium.dimensions.length/2 + 2) {
      // Ball is behind the goal line on blue side
      if (Math.abs(ballPosition.z) < this.stadium.dimensions.goalWidth/2) {
        // Ball is within the goal width
        if (ballPosition.y < this.stadium.dimensions.goalHeight) {
          // Orange team scored - ball is inside the blue goal
          this.score.orange++;
          this.goalScored('orange');
        }
      }
    } 
    // For orange goal (on the positive X side)
    else if (ballPosition.x > this.stadium.dimensions.length/2 - 2) {
      // Ball is behind the goal line on orange side
      if (Math.abs(ballPosition.z) < this.stadium.dimensions.goalWidth/2) {
        // Ball is within the goal width
        if (ballPosition.y < this.stadium.dimensions.goalHeight) {
          // Blue team scored - ball is inside the orange goal
          this.score.blue++;
          this.goalScored('blue');
        }
      }
    }
  }
  
  goalScored(team) {
    if (this.debug) {
      console.log(`Goal scored by ${team} team!`);
    }
    
    // Set game state
    this.currentState = this.states.GOAL;
    this.isPaused = true;
    
    // Update score display
    this.ui.updateScore(this.score.blue, this.score.orange);
    
    // Show goal message
    this.ui.showGoalMessage(team);
    
    // Reset ball and cars
    setTimeout(() => {
      this.ball.reset();
      this.playerCar.reset();
      this.aiCars.forEach(car => car.reset());
      
      // Start a new countdown after goal
      setTimeout(() => {
        this.isPaused = false;
        this.startCountdown();
      }, 500);
    }, 2000);
  }

  onResize() {
    if (this.scene) {
      this.scene.resize();
    }
  }

  cleanup() {
    // Remove event listeners
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.handleKeyPress);
    
    // Clear all intervals
    if (this.gameTimerInterval) {
      clearInterval(this.gameTimerInterval);
    }
    
    // Clean up input manager
    if (this.inputManager) {
      this.inputManager.cleanup();
      this.inputManager = null;
    }
    
    // Clean up physics world
    if (this.physicsWorld) {
      this.physicsWorld.cleanup && this.physicsWorld.cleanup();
      this.physicsWorld = null;
    }
    
    // Clean up scene last (after all objects have been removed)
    if (this.scene) {
      this.scene.cleanup && this.scene.cleanup();
      this.scene = null;
    }
    
    // Clear references to game objects
    this.playerCar = null;
    this.aiCars = [];
    this.bots = [];
    this.ball = null;
    this.boostPads = [];
    this.stadium = null;
    
    // Reset game state
    this.isGameActive = false;
    this.isPaused = false;
    
    if (this.debug) {
      console.log('Game completely cleaned up');
    }
  }

  createBoostPads() {
    // Initialize the boost pads array
    this.boostPads = [];
    
    // Layout the boost pads according to standard layout
    // 6 large boost pads, 30 small boost pads
    
    // Large boost pads - corners and mid side positions
    const largeBoostPositions = [
      { x: -40, y: 0.3, z: -25 }, // Blue Left
      { x: -40, y: 0.3, z: 25 },  // Blue Right
      { x: 0, y: 0.3, z: -35 },   // Mid Left
      { x: 0, y: 0.3, z: 35 },    // Mid Right
      { x: 40, y: 0.3, z: -25 },  // Orange Left
      { x: 40, y: 0.3, z: 25 }    // Orange Right
    ];
    
    // Small boost pads - spread across the field
    const smallBoostPositions = [
      // Center line
      { x: 0, y: 0.25, z: -15 },
      { x: 0, y: 0.25, z: 0 },
      { x: 0, y: 0.25, z: 15 },
      
      // Mid field lines
      { x: -20, y: 0.25, z: -15 },
      { x: -20, y: 0.25, z: 0 },
      { x: -20, y: 0.25, z: 15 },
      { x: 20, y: 0.25, z: -15 },
      { x: 20, y: 0.25, z: 0 },
      { x: 20, y: 0.25, z: 15 },
      
      // Goal areas
      { x: -30, y: 0.25, z: -10 },
      { x: -30, y: 0.25, z: 10 },
      { x: 30, y: 0.25, z: -10 },
      { x: 30, y: 0.25, z: 10 },
      
      // Side lanes
      { x: -15, y: 0.25, z: -25 },
      { x: -15, y: 0.25, z: 25 },
      { x: 15, y: 0.25, z: -25 },
      { x: 15, y: 0.25, z: 25 }
    ];
    
    // Create large boost pads
    largeBoostPositions.forEach(pos => {
      const boostPad = new BoostPad(this.scene, this.physicsWorld, pos, true);
      this.boostPads.push(boostPad);
    });
    
    // Create small boost pads
    smallBoostPositions.forEach(pos => {
      const boostPad = new BoostPad(this.scene, this.physicsWorld, pos, false);
      this.boostPads.push(boostPad);
    });
    
    if (this.debug) {
      console.log(`Created ${this.boostPads.length} boost pads (${largeBoostPositions.length} large, ${smallBoostPositions.length} small)`);
    }
  }
  
  updateBoostSystem() {
    // Skip if game is not active
    if (!this.isGameActive || this.isPaused) {
      return;
    }
    
    // Update all boost pads
    this.boostPads.forEach(pad => {
      pad.update();
      
      // Check if player car is collecting this pad
      const playerBoostAmount = pad.checkCollection(this.playerCar);
      if (playerBoostAmount > 0) {
        this.playerCar.boostAmount = Math.min(100, this.playerCar.boostAmount + playerBoostAmount);
        if (this.debug) {
          console.log(`Player collected boost: +${playerBoostAmount}, total: ${this.playerCar.boostAmount}`);
        }
      }
      
      // Check AI cars
      this.aiCars.forEach(car => {
        const aiBoostAmount = pad.checkCollection(car);
        if (aiBoostAmount > 0) {
          car.boostAmount = Math.min(100, car.boostAmount + aiBoostAmount);
          if (this.debug) {
            console.log(`AI collected boost: +${aiBoostAmount}, total: ${car.boostAmount}`);
          }
        }
      });
    });
  }
} 